import { notifications } from '@mantine/notifications'
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useAIFieldGeneration } from '@/hooks/useAIFieldGeneration'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import * as commonClasses from '@/styles/componentCommons.css'
import { type FAQItem, isFAQModified } from '@/types/faq'
import {
  categorizeFAQsForSaving,
  faqsToCreatePayload,
  faqsToUpdatePayload,
  markFAQsAsUpdated,
  reconcileCreatedFAQs,
} from '@/utils/faqHelpers'

export const FAQ_VALIDATION_ERROR = 'FAQ_VALIDATION_ERROR'

interface UseFAQManagementProps extends CMFormSection {
  type: 'cm' | 'step'
}

export interface FAQSectionRef {
  saveUnsavedFAQs: () => Promise<void>
}

/**
 * Hook for managing FAQ state and operations for both Content Modules and Steps.
 *
 * Handles:
 * - FAQ CRUD operations (create, read, update, delete)
 * - AI-powered FAQ generation (bulk and per-FAQ)
 * - Validation and error handling
 * - Server synchronization with optimistic updates
 *
 * @param props - Configuration including type ('cm' | 'step'), state, actions, and mutations
 * @returns FAQ state and handlers for managing FAQs
 */
export function useFAQManagement(props: UseFAQManagementProps) {
  // Get state and actions based on FAQ type
  const faqState = props.type === 'cm' ? props.cmFAQState : props.stepFAQState
  const actions = props.faqActions

  // Note: Two types of AI-generated FAQs exist in this system:
  // 1. pendingFAQs (from faqState) - Bulk-generated FAQs awaiting approval (legacy/unused)
  // 2. pendingSuggestion (on individual FAQItem) - Per-FAQ AI suggestions (current approach)
  // These are mutually exclusive patterns and currently only per-FAQ generation is used.

  const setApprovedFAQs = props.type === 'cm' ? actions.setCMApprovedFAQs : actions.setStepApprovedFAQs
  const setPendingFAQs = props.type === 'cm' ? actions.setCMPendingFAQs : actions.setStepPendingFAQs
  const setShowValidationErrors =
    props.type === 'cm' ? actions.setCMShowValidationErrors : actions.setStepShowValidationErrors
  const setIsInitialized = props.type === 'cm' ? actions.setCMIsInitialized : actions.setStepIsInitialized

  const { approved: approvedFAQs, pending: pendingFAQs, showValidationErrors, isInitialized } = faqState

  const { getFieldState, generateFieldByName, rejectSuggestion } = useAIFieldGeneration(props)

  const prevContextRef = useRef({ stepId: props.stepId, taskId: props.taskId, type: props.type })

  const hasLocalChanges = useMemo(() => {
    return (
      approvedFAQs.some(
        faq =>
          faq.isNew ||
          faq.isPending ||
          faq.isDeleted ||
          (faq.originalQuestion !== undefined && faq.question !== faq.originalQuestion) ||
          (faq.originalAnswer !== undefined && faq.answer !== faq.originalAnswer),
      ) || pendingFAQs.length > 0
    )
  }, [approvedFAQs, pendingFAQs])

  // Reset state when context changes (different CM/step)
  useEffect(() => {
    const prev = prevContextRef.current
    const contextChanged =
      props.type !== prev.type ||
      (props.type === 'step' && props.stepId !== prev.stepId) ||
      (props.type === 'cm' && props.taskId !== prev.taskId)

    if (contextChanged) {
      setApprovedFAQs([])
      setPendingFAQs([])
      setShowValidationErrors(false)
      setIsInitialized(false)
      prevContextRef.current = { stepId: props.stepId, taskId: props.taskId, type: props.type }
    }
  }, [
    props.stepId,
    props.taskId,
    props.type,
    setApprovedFAQs,
    setPendingFAQs,
    setShowValidationErrors,
    setIsInitialized,
  ])

  // Initialize from server data only when first loading
  useEffect(() => {
    const existingFAQs = props.type === 'cm' ? props.cmFAQsQuery.data : props.stepFAQsQuery.data
    const hasExistingFAQs = existingFAQs && existingFAQs.length > 0
    const contentQueriesResolved =
      props.type === 'step'
        ? props.stepContentQuery?.isSuccess
        : props.stepsQuery?.isSuccess && props.cmQuery?.isSuccess

    // Wait for FAQ query to resolve, and if no existing FAQs, also wait for content queries
    const shouldInitialize =
      !isInitialized && !hasLocalChanges && existingFAQs !== undefined && (hasExistingFAQs || contentQueriesResolved)

    if (!shouldInitialize) return

    if (hasExistingFAQs) {
      const serverFAQs: FAQItem[] = existingFAQs.map(faq => ({
        id: faq.faqId,
        question: faq.question,
        answer: faq.answer,
        isNew: false,
        originalQuestion: faq.question,
        originalAnswer: faq.answer,
      }))
      setApprovedFAQs(serverFAQs)
      setPendingFAQs([])
    } else {
      const hasContent = props.type === 'step' ? !!props.stepContentQuery?.data : !!props.stepsQuery?.data?.length
      // Steps: do not auto-generate FAQ on content upload or first load; show one empty slot only.
      const shouldAutoGenerate = props.type === 'cm' && hasContent

      const newFaqId = nanoid()
      const newFAQ: FAQItem = {
        id: newFaqId,
        question: '',
        answer: '',
        isNew: true,
        isGenerating: shouldAutoGenerate,
      }
      setApprovedFAQs([newFAQ])
      setPendingFAQs([])

      if (shouldAutoGenerate) {
        generateFieldByName(props.type, 'faqSingle', {
          faqId: newFaqId,
          faqList: [newFAQ],
        })
      }
    }
    setIsInitialized(true)
  }, [
    props.type,
    props.cmFAQsQuery.data,
    props.stepFAQsQuery.data,
    props.stepContentQuery?.isSuccess,
    props.stepContentQuery?.data,
    props.stepsQuery?.isSuccess,
    props.stepsQuery?.data?.length,
    props.cmQuery?.isSuccess,
    isInitialized,
    hasLocalChanges,
    setApprovedFAQs,
    setPendingFAQs,
    setIsInitialized,
    generateFieldByName,
  ])

  // Trigger AI generation for newly added FAQs
  useEffect(() => {
    if (pendingGenerationRef.current.size > 0) {
      const faqIdsToGenerate = Array.from(pendingGenerationRef.current)
      pendingGenerationRef.current.clear()

      faqIdsToGenerate.forEach(faqId => {
        const faq = approvedFAQs.find(f => f.id === faqId)
        if (faq) {
          generateFieldByName(props.type, 'faqSingle', {
            faqId,
            faqList: approvedFAQs,
          })
        }
      })
    }
  }, [approvedFAQs, generateFieldByName, props.type])

  // Handle per-FAQ AI generation results
  useEffect(() => {
    const fieldState = getFieldState('faqSingle')
    if (fieldState.state === 'pending' && fieldState.suggestion) {
      const generatingFAQ = approvedFAQs.find(faq => faq.isGenerating)

      if (generatingFAQ) {
        setApprovedFAQs(prev =>
          prev.map(faq =>
            faq.id === generatingFAQ.id
              ? { ...faq, pendingSuggestion: fieldState.suggestion ?? undefined, isGenerating: false }
              : faq,
          ),
        )
        rejectSuggestion('faqSingle')
      } else {
        console.warn('FAQ generation completed without a generating FAQ')
        // Clean up any FAQs that might be stuck in generating state
        setApprovedFAQs(prev => prev.map(faq => (faq.isGenerating ? { ...faq, isGenerating: false } : faq)))
        rejectSuggestion('faqSingle')
      }
    }
  }, [getFieldState, rejectSuggestion, approvedFAQs, setApprovedFAQs])

  // Per-FAQ generation handler (defined early so handleAddFAQ can use it)
  const handleGenerateFAQForId = useCallback(
    (faqId: string) => {
      // Set loading state for this FAQ
      setApprovedFAQs(prev => {
        const updated = prev.map(faq => (faq.id === faqId ? { ...faq, isGenerating: true } : faq))

        generateFieldByName(props.type, 'faqSingle', {
          faqId,
          faqList: updated,
        })

        return updated
      })
    },
    [generateFieldByName, props.type, setApprovedFAQs],
  )

  const handleGenerateFAQForIdWithGuide = useCallback(
    (faqId: string, instructions: string) => {
      setApprovedFAQs(prev => {
        const updated = prev.map(faq => (faq.id === faqId ? { ...faq, isGenerating: true } : faq))

        generateFieldByName(props.type, 'faqSingle', {
          faqId,
          faqList: updated,
          user_instructions: instructions.trim(),
        })

        return updated
      })
    },
    [generateFieldByName, props.type, setApprovedFAQs],
  )

  // Track FAQs that need AI generation after being added
  const pendingGenerationRef = useRef<Set<string>>(new Set())

  const handleAddFAQ = useCallback(
    (autoGenerate: boolean = true) => {
      const newId = nanoid()
      const newFAQ: FAQItem = {
        id: newId,
        question: '',
        answer: '',
        isNew: true,
        isGenerating: autoGenerate,
      }

      if (autoGenerate) {
        pendingGenerationRef.current.add(newId)
      }
      setApprovedFAQs(prev => [newFAQ, ...prev])
    },
    [setApprovedFAQs],
  )

  const handleUpdateFAQ = useCallback(
    (id: string, field: 'question' | 'answer', value: string) => {
      setApprovedFAQs(prev => prev.map(faq => (faq.id === id ? { ...faq, [field]: value } : faq)))
      setShowValidationErrors(false)
    },
    [setApprovedFAQs, setShowValidationErrors],
  )

  const handleDeleteFAQ = useCallback(
    async (id: string) => {
      // Mark the FAQ as deleted locally - don't call the API immediately
      setApprovedFAQs(prev => prev.map(faq => (faq.id === id ? { ...faq, isDeleted: true } : faq)))
      setPendingFAQs(prev => prev.filter(f => f.id !== id)) // Pending FAQs can be removed immediately since they're not saved
    },
    [setApprovedFAQs, setPendingFAQs],
  )

  // Per-FAQ generation handlers (handleGenerateFAQForId defined above)
  const handleAcceptFAQSuggestion = useCallback(
    (faqId: string) => {
      setApprovedFAQs(prev =>
        prev.map(faq => {
          if (faq.id === faqId && faq.pendingSuggestion) {
            try {
              const generatedFAQ = JSON.parse(faq.pendingSuggestion) as { question: string; answer: string }
              // Apply the suggestion and clear the pending state
              return {
                ...faq,
                question: generatedFAQ.question,
                answer: generatedFAQ.answer,
                pendingSuggestion: undefined,
                isGenerating: false,
              }
            } catch (error) {
              console.error('Failed to apply FAQ suggestion:', error)
              return { ...faq, pendingSuggestion: undefined, isGenerating: false }
            }
          }
          return faq
        }),
      )
    },
    [setApprovedFAQs],
  )

  const handleRejectFAQSuggestion = useCallback(
    (faqId: string) => {
      // Clear the pending suggestion, keep the original values
      setApprovedFAQs(prev =>
        prev.map(faq => (faq.id === faqId ? { ...faq, pendingSuggestion: undefined, isGenerating: false } : faq)),
      )
    },
    [setApprovedFAQs],
  )

  const handleRegenerateFAQ = useCallback(
    (faqId: string) => {
      // The current pendingSuggestion will be passed as proposed_data automatically
      handleGenerateFAQForId(faqId)
    },
    [handleGenerateFAQForId],
  )

  const getFieldStateForFAQ = useCallback(
    (faqId: string) => {
      const faq = approvedFAQs.find(f => f.id === faqId)

      if (!faq) {
        return { state: 'idle' as const, suggestion: null }
      }

      if (faq.isGenerating) {
        return { state: 'loading' as const, suggestion: null }
      }

      if (faq.pendingSuggestion) {
        return { state: 'pending' as const, suggestion: faq.pendingSuggestion }
      }

      return { state: 'idle' as const, suggestion: null }
    },
    [approvedFAQs],
  )

  /**
   * Saves all unsaved FAQ changes to the server.
   *
   * Handles:
   * - Validates all FAQs (incomplete FAQs will trigger validation errors)
   * - Creates new FAQs
   * - Updates modified existing FAQs
   * - Deletes FAQs marked for deletion
   * - Removes empty new FAQs silently
   *
   * @throws {Error} FAQ_VALIDATION_ERROR if any FAQs are incomplete
   * @throws {Error} If required mutations are not available or API calls fail
   */
  const saveUnsavedFAQs = useCallback(async () => {
    try {
      // Categorize FAQs into different buckets for processing
      const categorized = categorizeFAQsForSaving(approvedFAQs)

      // Validate - check for incomplete FAQs
      if (categorized.incompleteFAQs.length > 0) {
        setShowValidationErrors(true)
        notifications.show({
          title: 'Cannot Save - Incomplete FAQs',
          message: `Please complete all FAQ fields before saving. ${categorized.incompleteFAQs.length} FAQ(s) are missing either a question or answer.`,
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
        throw new Error(FAQ_VALIDATION_ERROR)
      }

      // Remove completely empty new FAQs silently
      if (categorized.emptyNewFAQs.length > 0) {
        setApprovedFAQs(prev => prev.filter(faq => !categorized.emptyNewFAQs.some(empty => empty.id === faq.id)))
      }

      // Early return if nothing to save
      if (
        categorized.validNewFAQs.length === 0 &&
        categorized.validExistingFAQs.length === 0 &&
        categorized.deletedFAQs.length === 0
      ) {
        setShowValidationErrors(false)
        return
      }

      if (props.type === 'cm' && !props.taskId) {
        throw new Error('Task ID is required for content module FAQs')
      }
      if (props.type === 'step' && !props.stepId) {
        throw new Error('Step ID is required for step FAQs')
      }

      const idParams = props.type === 'cm' ? { task_id: props.taskId! } : { step_id: props.stepId! }

      // Delete FAQs
      if (categorized.deletedFAQs.length > 0) {
        if (!props.faqsDeleteMutation) {
          throw new Error('Delete mutation not available')
        }
        await props.faqsDeleteMutation.mutateAsync({
          ...idParams,
          faq_ids: categorized.deletedFAQs.map(faq => faq.id),
        })
      }

      // Create new FAQs
      if (categorized.validNewFAQs.length > 0) {
        if (!props.faqsCreateMutation) {
          throw new Error('Create mutation not available')
        }
        const createResponse = await props.faqsCreateMutation.mutateAsync({
          ...idParams,
          faqs: faqsToCreatePayload(categorized.validNewFAQs),
        })

        // Reconcile local FAQs with server-returned IDs
        setApprovedFAQs(prev => reconcileCreatedFAQs(prev, createResponse))
      }

      // Update existing FAQs
      if (categorized.validExistingFAQs.length > 0) {
        if (!props.faqsUpdateMutation) {
          throw new Error('Update mutation not available')
        }
        await props.faqsUpdateMutation.mutateAsync({
          ...idParams,
          faqs: faqsToUpdatePayload(categorized.validExistingFAQs),
        })

        // Mark FAQs as saved (sync original values)
        setApprovedFAQs(prev =>
          markFAQsAsUpdated(
            prev,
            categorized.validExistingFAQs.map(faq => faq.id),
          ),
        )
      }

      // Remove deleted FAQs from local state
      if (categorized.deletedFAQs.length > 0) {
        setApprovedFAQs(prev => prev.filter(faq => !faq.isDeleted))
      }

      setShowValidationErrors(false)
    } catch (error) {
      console.error('Failed to save FAQs:', error)
      throw error
    }
  }, [
    approvedFAQs,
    props.type,
    props.taskId,
    props.stepId,
    props.faqsCreateMutation,
    props.faqsUpdateMutation,
    props.faqsDeleteMutation,
    setApprovedFAQs,
    setShowValidationErrors,
  ])

  const resetFAQState = useCallback(() => {
    setApprovedFAQs([])
    setPendingFAQs([])
    setShowValidationErrors(false)
    setIsInitialized(false)
  }, [setApprovedFAQs, setPendingFAQs, setShowValidationErrors, setIsInitialized])

  const visibleFAQs = useMemo(() => approvedFAQs.filter(faq => !faq.isDeleted), [approvedFAQs])

  const hasUnsavedChanges = useMemo(
    () =>
      approvedFAQs.some(
        faq =>
          (faq.isNew && (faq.question.trim().length > 0 || faq.answer.trim().length > 0)) ||
          isFAQModified(faq) ||
          faq.isDeleted,
      ),
    [approvedFAQs],
  )

  const hasIncompleteChanges = useMemo(
    () =>
      approvedFAQs.some(faq => {
        if (faq.isDeleted) return false
        const hasQuestion = faq.question.trim().length > 0
        const hasAnswer = faq.answer.trim().length > 0
        return !(hasQuestion && hasAnswer)
      }),
    [approvedFAQs],
  )

  return {
    // State - filter out deleted FAQs from the UI
    approved: visibleFAQs,
    showValidationErrors,
    hasUnsavedChanges,
    hasIncompleteChanges,
    totalCount: visibleFAQs.length,

    // CRUD actions
    handleAddFAQ,
    handleUpdateFAQ,
    handleDeleteFAQ,
    saveUnsavedFAQs,
    resetFAQState,

    // Per-FAQ AI generation actions
    handleGenerateFAQForId,
    handleGenerateFAQForIdWithGuide,
    handleAcceptFAQSuggestion,
    handleRejectFAQSuggestion,
    handleRegenerateFAQ,
    getFieldStateForFAQ,
  }
}
